import React, { useState } from "react";
import "../documentation.css";
import moment from "moment";
import { DoneIcon, TodoIcon } from "../../assets/Icons";
import FadingSpinner from "../../common/FadingSpinner/FadingSpinner";
import { filenameFromUrl, formatTimestamp, getLatestDate } from "../helpers";

const MobileDownload = ({ getPdf, form, isIncomplete = false, timestamp }) => {
  const [isDownloading, setDownloading] = useState(false);
  const handleDownloadClick = async (count = 0) => {
    setDownloading(false);
    if (!isDownloading && count < 3) {
      setDownloading(true);
      try {
        await getPdf();
        setDownloading(false);
      } catch (err) {
        setTimeout(() => {
          handleDownloadClick(count + 1);
        }, 2000);
      }
    }
  };
  return (
    <div className="documentation-mobile__readingRecord">
      <div>
        <b>{form.title}</b>
        {!isIncomplete && (
          <div className="documentation-mobile__readingRecord-subtitle">
            Signed {timestamp}
          </div>
        )}
      </div>
      <div className="documentation-mobile__readingRecord-action">
        <button
          className="reading-list__record-form-button d-flex justify-content-center align-items-center"
          onClick={() => handleDownloadClick()}
        >
          {/* eslint-disable-next-line */}
          {isDownloading ? (
            <FadingSpinner size="sm" />
          ) : isIncomplete ? (
            "See progress"
          ) : (
            "Download"
          )}
        </button>
      </div>
    </div>
  );
};

const UserDocElem = ({
  candidate,
  readingList,
  getFormPdf,
  getChecklistPdf,
  onlineForms,
  checklists
}) => {
  const [isExpanded, setExpanded] = useState(false);
  const toggleExpand = () => setExpanded(!isExpanded);
  const checkIfAllDocsDone = () => {
    const docsDone = readingList.map(doc => {
      const filename = filenameFromUrl(doc.link);
      if (
        candidate.documents &&
        candidate.documents.readings &&
        candidate.documents.readings[filename]
      ) {
        return true;
      }
      return false;
    });
    const formsDone = onlineForms.map(form => {
      if (
        candidate.documents &&
        candidate.documents.forms &&
        candidate.documents.forms[form.code]
      ) {
        return true;
      }
      return false;
    });
    const listsDone = checklists.map(list => {
      if (
        candidate.documents &&
        candidate.documents.checklists &&
        candidate.documents.checklists[list.code]
      ) {
        const datesArray = Object.values(
          candidate.documents.checklists[list.code]
        );
        const parsedDates = datesArray.flatMap(date =>
          date ? moment(date) : []
        );
        return parsedDates.length === 19;
      }
      return false;
    });
    return (
      !docsDone.includes(false) &&
      !formsDone.includes(false) &&
      !listsDone.includes(false)
    );
  };
  return (
    <div className="documentation-mobile__elem-wrapper">
      <div className="w-100 documentation-mobile__elem" onClick={toggleExpand}>
        <div className="d-flex justify-content-between">
          <b>
            <span className="documentation-mobile__totalIcon">
              {checkIfAllDocsDone() ? <DoneIcon /> : <TodoIcon />}
            </span>
            {candidate.firstName} {candidate.lastName}
          </b>
          <span className="documentation-mobile__arrows">
            {isExpanded ? (
              <span className="documentation-mobile__arrowup"></span>
            ) : (
              <span className="documentation-mobile__arrowbottom"></span>
            )}
          </span>
        </div>
      </div>
      {isExpanded && (
        <div className="documentation-mobile__expanded">
          {readingList.map(doc => {
            const filename = filenameFromUrl(doc.link);
            if (
              candidate.documents &&
              candidate.documents.readings &&
              candidate.documents.readings[filename]
            ) {
              return (
                <div className="documentation-mobile__readingRecord">
                  <div>
                    <b>{doc.title}</b>
                    <div className="documentation-mobile__readingRecord-subtitle">
                      Signed{" "}
                      {formatTimestamp(candidate.documents.readings[filename])}
                    </div>
                  </div>
                  <div className="documentation-mobile__readingRecord-action">
                    <DoneIcon />
                  </div>
                </div>
              );
            }
            return (
              <div className="documentation-mobile__readingRecord">
                <div>
                  <b>{doc.title}</b>
                </div>
                <div className="documentation-mobile__readingRecord-action">
                  <TodoIcon />
                </div>
              </div>
            );
          })}
          {onlineForms.map(form => {
            if (
              candidate.documents &&
              candidate.documents.forms &&
              candidate.documents.forms[form.code]
            ) {
              return (
                <MobileDownload
                  getPdf={() =>
                    getFormPdf(
                      form.code,
                      candidate.candidateRef,
                      form.title,
                      candidate.firstName,
                      candidate.lastName
                    )
                  }
                  candidate={candidate}
                  form={form}
                  timestamp={formatTimestamp(
                    candidate.documents.forms[form.code].timestamp
                  )}
                />
              );
            }
            return (
              <div className="documentation-mobile__readingRecord">
                <div>
                  <b>{form.title}</b>
                </div>
                <div className="documentation-mobile__readingRecord-action">
                  <TodoIcon />
                </div>
              </div>
            );
          })}
          {checklists.map(list => {
            if (
              candidate.documents &&
              candidate.documents.checklists &&
              candidate.documents.checklists[list.code]
            ) {
              const datesArray = Object.values(
                candidate.documents.checklists[list.code]
              );
              const parsedDates = datesArray.flatMap(date =>
                date ? moment(date) : []
              );
              const isIncomplete =
                parsedDates.length > 0 && parsedDates.length < 19;
              return (
                <MobileDownload
                  getPdf={() =>
                    getChecklistPdf(
                      candidate.candidateRef,
                      list.title,
                      candidate.firstName,
                      candidate.lastName
                    )
                  }
                  candidate={candidate}
                  form={list}
                  timestamp={formatTimestamp(getLatestDate(datesArray))}
                  isIncomplete={isIncomplete}
                />
              );
            }
            return (
              <div className="documentation-mobile__readingRecord">
                <div>
                  <b>{list.title}</b>
                </div>
                <div className="documentation-mobile__readingRecord-action">
                  <TodoIcon />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const MobileDocumentation = ({
  readingList,
  onlineForms,
  checklists,
  candidates,
  getFormPdf,
  getChecklistPdf
}) => (
  <section className="d-block d-md-none documentation-mobile__wrapper">
    {candidates.map(candidate => (
      <UserDocElem
        key={candidate.candidateRef}
        candidate={candidate}
        readingList={readingList}
        checklists={checklists}
        getFormPdf={getFormPdf}
        getChecklistPdf={getChecklistPdf}
        onlineForms={onlineForms}
      />
    ))}
  </section>
);

export default MobileDocumentation;
